import { ensureMapObjectPane } from '../../core/map-object-focus';
/**
 * ProspectGridLayer
 *
 * Owns the area-prospecting grid overlay: two canvas layers drawing a
 * two-tier Web Mercator grid that is one coordinate system with the basemap —
 *   - solid major lines = the basemap's own tiles (tracks map zoom), and
 *   - dotted minor lines = the selectable cells at a fixed absolute zoom.
 * The cells are addressed at an absolute tile zoom (not relative to the map), so a
 * selection survives panning and zooming. They show only once they are large
 * enough on screen, so the dotted grid is never too dense.
 *
 * Pure logic + rendering only: it binds NO map mouse events. Selection gestures
 * arrive through MapInteractionController (the one gesture owner), which calls
 * ProspectFeature's SelectableFeature methods. The feature adds/removes this
 * Leaflet layer through render/clear; prospect-control drives cell size / colour / Pull.
 */
import * as L from 'leaflet';
import { Subscription } from 'rxjs';
import { GridObjectSelection } from '../../core/grid-object-selection';
import { PullCoverageCell } from '../../../services/place-pull.service';
import { PullCoverageStyles } from './pull-coverage-style';
import { latLngToTile } from '../../core/spatial/tile-math';

interface TileRange { minX: number; maxX: number; minY: number; maxY: number; }

/** Internal canvas surface; Leaflet mounting owns its display and event lifetime. */
class GridCanvasLayer extends L.Layer {
  private canvas?: HTMLCanvasElement;
  private attachedMap?: L.Map;

  get visible(): boolean {
    return !!this.canvas?.isConnected && this.canvas.style.visibility !== 'hidden';
  }

  constructor(private grid: ProspectGridLayer, private readonly selectionOnly = false) {
    super();
  }

  override onAdd(map: L.Map): this {
    this.attachedMap = map;
    const canvas = L.DomUtil.create('canvas', 'prospect-grid-canvas') as HTMLCanvasElement;
    canvas.style.position = 'absolute';
    canvas.style.pointerEvents = 'none'; // map handles the clicks; canvas only paints
    const pane = this.selectionOnly
      ? ensureMapObjectPane(map, 'map-object-selection', 620) : map.getPanes().overlayPane;
    pane.appendChild(canvas);
    this.canvas = canvas;

    // Redraw on settle only; during a pan the canvas translates with the overlay
    // pane, so per-frame redraws aren't needed.
    map.on('moveend zoomend resize', this.redraw, this);
    map.on('zoomstart', this.hide, this);
    this.redraw();
    return this;
  }

  override onRemove(map: L.Map): this {
    map.off('moveend zoomend resize', this.redraw, this);
    map.off('zoomstart', this.hide, this);
    this.canvas?.remove();
    this.canvas = undefined;
    this.attachedMap = undefined;
    return this;
  }

  private hide = () => {
    if (this.canvas) this.canvas.style.visibility = 'hidden';
  };

  redraw = () => {
    const map = this.attachedMap;
    const canvas = this.canvas;
    if (!map || !canvas) return;

    canvas.style.visibility = 'visible';
    const size = map.getSize();
    // Render at device-pixel resolution so the thin lines stay crisp on hiDPI
    // screens (the canvas is then drawn in CSS px via the scale transform below).
    const dpr = window.devicePixelRatio || 1;
    const needW = Math.round(size.x * dpr), needH = Math.round(size.y * dpr);
    if (canvas.width !== needW || canvas.height !== needH) {
      canvas.width = needW;
      canvas.height = needH;
      canvas.style.width = `${size.x}px`;
      canvas.style.height = `${size.y}px`;
    }
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.x, size.y);

    const color = this.grid.color;
    const W = size.x, H = size.y;
    const b = map.getBounds();
    const selectedCells = [...this.grid.selectedCellsInBounds(b)];

    // Tile boundary → container px, for a tile scheme of 2^z tiles across.
    const xAt = (tx: number, nn: number) => map.latLngToContainerPoint(L.latLng(0, tx / nn * 360 - 180)).x;
    const yAt = (ty: number, nn: number) =>
      map.latLngToContainerPoint(L.latLng(Math.atan(Math.sinh(Math.PI * (1 - 2 * ty / nn))) * 180 / Math.PI, 0)).y;
    const rangeAt = (z: number): TileRange => {
      const nw = latLngToTile(b.getNorth(), b.getWest(), z);
      const se = latLngToTile(b.getSouth(), b.getEast(), z);
      return {
        minX: Math.min(nw.tileX, se.tileX), maxX: Math.max(nw.tileX, se.tileX),
        minY: Math.min(nw.tileY, se.tileY), maxY: Math.max(nw.tileY, se.tileY),
      };
    };

    const sZoom = this.grid.solidZoom(map);   // basemap tile
    const cZoom = this.grid.cellZoomLevel;     // current brush cell zoom
    const sN = 2 ** sZoom, cN = 2 ** cZoom;

    // Spotlight scrim: dim the whole basemap, then punch the selection back
    // out as bright windows so the eye lands on the chosen
    // area. Drawn first; the selection fills + grid lines paint over it. Only the
    // basemap is dimmed (this canvas sits below the marker pane).
    if (!this.selectionOnly && this.grid.dim) {
      ctx.globalAlpha = 1;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'destination-out';
      for (const { z, x, y } of selectedCells) {
        const nn = 2 ** z;
        const px = xAt(x, nn), py = yAt(y, nn);
          ctx.fillRect(px, py, xAt(x + 1, nn) - px, yAt(y + 1, nn) - py);
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    // Selected cells (any size, mixed) — always visible as a translucent fill,
    // no border (cleaner against the basemap). When dimming, the spotlight punch-
    // out already marks the selection, so keep only a hint of blue or the bright
    // window gets darkened back down.
    if (this.selectionOnly) {
      ctx.fillStyle = color;
      ctx.globalAlpha = this.grid.dim ? 0.12 : 0.3;
      for (const { z, x, y } of selectedCells) {
        const nn = 2 ** z;
        const px = xAt(x, nn), py = yAt(y, nn);
        ctx.fillRect(px, py, xAt(x + 1, nn) - px, yAt(y + 1, nn) - py);
      }

      ctx.globalAlpha = 1;
      return;
    }

    // Historical coverage is beneath selection/focus and never handles input.
    // Opposite hatches keep both sources legible even on identical cell bounds.
    for (const style of PullCoverageStyles) {
      const patternCanvas = document.createElement('canvas');
      patternCanvas.width = patternCanvas.height = 12;
      const patternContext = patternCanvas.getContext('2d')!;
      patternContext.strokeStyle = style.color;
      patternContext.lineWidth = 1.5;
      patternContext.beginPath();
      const rising = style.angle === 45;
      for (const offset of [-12, 0, 12]) {
        patternContext.moveTo(offset, rising ? 12 : 0);
        patternContext.lineTo(offset + 12, rising ? 0 : 12);
      }
      patternContext.stroke();
      const pattern = ctx.createPattern(patternCanvas, 'repeat');
      if (!pattern) continue;
      for (const cell of this.grid.coverage) {
        if (cell.source !== style.source) continue;
        const n = 2 ** cell.zoom;
        const top = yAt(cell.tileY, n), bottom = yAt(cell.tileY + 1, n);
        if (bottom < 0 || top > H) continue;
        // Leaflet permits panning into wrapped copies of the world.
        for (let world = Math.floor((b.getWest() + 180) / 360);
          world <= Math.floor((b.getEast() + 180) / 360); world++) {
          const left = xAt(cell.tileX + world * n, n);
          const right = xAt(cell.tileX + 1 + world * n, n);
          if (right < 0 || left > W) continue;
          const width = right - left, height = bottom - top;
          ctx.globalAlpha = 0.1;
          ctx.fillStyle = style.color;
          ctx.fillRect(left, top, width, height);
          ctx.globalAlpha = 0.65;
          ctx.fillStyle = pattern;
          ctx.fillRect(left, top, width, height);
        }
      }
    }

    // Draw selectable cells whenever they are large enough on screen.
    if (this.grid.cellsLargeEnoughAt(map)) {
      const cRange = rangeAt(cZoom);
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.8;
      ctx.lineWidth = 2;        // dot diameter (round cap)
      ctx.lineCap = 'round';
      ctx.setLineDash([0, 6]);  // dotted: a round dot every 6px
      ctx.beginPath();
      for (let tx = cRange.minX; tx <= cRange.maxX + 1; tx++) {
        const x = xAt(tx, cN);
        ctx.moveTo(x, 0); ctx.lineTo(x, H);
      }
      for (let ty = cRange.minY; ty <= cRange.maxY + 1; ty++) {
        const y = yAt(ty, cN);
        ctx.moveTo(0, y); ctx.lineTo(W, y);
      }
      ctx.stroke();
    }

    // Major lines follow the basemap tiles while this layer is mounted.
    const sRange = rangeAt(sZoom);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.8;
    ctx.lineWidth = 1.25;     // continuous, same colour / opacity as the dotted minor
    ctx.lineCap = 'butt';
    ctx.setLineDash([]);
    ctx.beginPath();
    for (let tx = sRange.minX; tx <= sRange.maxX + 1; tx++) {
      const x = xAt(tx, sN);
      ctx.moveTo(x, 0); ctx.lineTo(x, H);
    }
    for (let ty = sRange.minY; ty <= sRange.maxY + 1; ty++) {
      const y = yAt(ty, sN);
      ctx.moveTo(0, y); ctx.lineTo(W, y);
    }
    ctx.stroke();

    // Drag feedback belongs to MapInteractionController, not here — the grid
    // canvas only renders the persistent selection and guide grids.

    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
  };
}

export class ProspectGridLayer extends L.LayerGroup {
  /** Absolute Web Mercator zoom of a selectable cell. Fixed (not relative to the
   *  map zoom) so a selection survives panning and zooming. Larger zoom = smaller cell. */
  cellZoomLevel = 15;

  /** Whether to dim the basemap outside the selection (a spotlight on the chosen
   *  cells, so the user focuses on the selection area). A panel option, like
   *  grid colour; drawn only while the Prospect layer is visible. */
  dim = false;

  /** Grid line colour (hex), user-configurable. */
  color = '#2c5fb3';

  /** Zoom offset of the basemap tile scheme vs the map zoom (Mapbox 512 / zoomOffset -1). */
  basemapTileZoomOffset = -1;

  /** Cells appear / are selectable only once each is at least this many px on
   *  screen, so the dotted grid is never too dense. */
  private static readonly MIN_CELL_PX = 12;

  coverage: readonly PullCoverageCell[] = [];

  setCoverage(cells: readonly PullCoverageCell[]): void {
    this.coverage = cells;
    this.redraw();
  }

  private attachedMap?: L.Map;
  private readonly gridCanvas = new GridCanvasLayer(this);
  private readonly selectionCanvas = new GridCanvasLayer(this, true);
  private selectionChanges?: Subscription;

  constructor(private readonly selection: GridObjectSelection) {
    super();
    this.addLayer(this.gridCanvas);
    this.addLayer(this.selectionCanvas);
  }

  override onAdd(map: L.Map): this {
    this.attachedMap = map;
    super.onAdd(map);
    this.selectionChanges = this.selection.changed$.subscribe(() => {
      // Painting selection must not repaint thousands of historical cells.
      // Only the optional spotlight depends on selection in the base canvas.
      if (this.dim) this.gridCanvas.redraw();
      this.selectionCanvas.redraw();
    });
    return this;
  }

  override onRemove(map: L.Map): this {
    this.selectionChanges?.unsubscribe();
    this.selectionChanges = undefined;
    if (this.colorRedrawTimer) clearTimeout(this.colorRedrawTimer);
    this.colorRedrawTimer = undefined;
    this.attachedMap = undefined;
    return super.onRemove(map);
  }

  private redraw(): void {
    this.gridCanvas.redraw();
    this.selectionCanvas.redraw();
  }

  /** The basemap tile zoom (solid lines) at the current view. */
  solidZoom(map: L.Map): number {
    return Math.round(map.getZoom()) + this.basemapTileZoomOffset;
  }

  /** On-screen px size of one cell at the current map view. */
  private cellPxAt(map: L.Map): number {
    const n = 2 ** this.cellZoomLevel;
    const b = map.getBounds();
    const t = latLngToTile(b.getNorth(), b.getWest(), this.cellZoomLevel);
    const x0 = map.latLngToContainerPoint(L.latLng(0, t.tileX / n * 360 - 180)).x;
    const x1 = map.latLngToContainerPoint(L.latLng(0, (t.tileX + 1) / n * 360 - 180)).x;
    return Math.abs(x1 - x0) || 1;
  }

  /** Whether brush cells are large enough on screen to draw / select without the
   *  grid collapsing into noise. */
  cellsLargeEnoughAt(map: L.Map): boolean {
    return this.cellPxAt(map) >= ProspectGridLayer.MIN_CELL_PX;
  }

  /** Whether brush cells are large enough to select at the current view. */
  canSelectAt(map: L.Map): boolean {
    return this.cellsLargeEnoughAt(map);
  }

  canSelect(): boolean {
    return !!this.attachedMap && this.gridCanvas.visible && this.canSelectAt(this.attachedMap);
  }

  /** Dim-surroundings option (spotlight the selection). Independent of the grid
   *  lines; the ProspectFeature gates it on the layer being visible. */
  setDim(on: boolean): void {
    this.dim = on;
    this.redraw();
  }

  setCellZoom(zoom: number): void {
    // Picking a size only changes the brush. The mixed-size selection remains.
    this.cellZoomLevel = Number(zoom);
    this.redraw();
  }

  private colorRedrawTimer?: ReturnType<typeof setTimeout>;
  setColor(hex: string): void {
    this.color = hex;
    // Colour drags fire `input` continuously; throttle to ~25fps so the native
    // picker stays responsive. this.color is set synchronously, so the trailing
    // redraw always paints the final colour.
    if (!this.attachedMap || this.colorRedrawTimer) return;
    this.colorRedrawTimer = setTimeout(() => {
      this.colorRedrawTimer = undefined;
      this.redraw();
    }, 40);
  }

  // --- helpers used by the canvas layer ---

  *selectedCellsInBounds(
    bounds: L.LatLngBounds,
  ): Iterable<{ z: number; x: number; y: number }> {
    for (const cell of this.selection.cellsInBounds(bounds)) {
      yield { z: cell.zoom, x: cell.tileX, y: cell.tileY };
    }
  }
}
